import {
    CommandResponse,
    InclusionSection,
    PlacementPayload,
    RenamePayload,
    RevisionedCommandResponse,
    StudyEntityType,
    StudySection,
    toInclusionSection,
} from '@faro/study-designer-model';
import axios from 'axios';
import { createStudyEntityHookFactory } from '../createStudyEntityHookFactory';
import { StudyPathParameter } from '../parameter';

const factory = createStudyEntityHookFactory<InclusionSection>(StudyEntityType.InclusionSection);

export const useInclusionSections = factory.list(
    (
        { studyId, ...query }: StudyPathParameter & { revision?: number },
        signal?: AbortSignal
    ): Promise<InclusionSection[]> => {
        return axios
            .get(`/api/study-designer/studies/${studyId}/inclusion-sections`, {
                signal,
                params: query,
            })
            .then(response => (response.data ?? []).map(toInclusionSection));
    }
);

export const useInclusionSectionUpdate = factory.mutate(
    (entity: InclusionSection): Promise<RevisionedCommandResponse<CommandResponse>> => {
        return axios
            .put(`/api/study-designer/studies/${entity.studyId}/inclusion-sections/${entity.id}`, entity)
            .then(response => response.data);
    },
    {
        optimisticUpdate: (inclusionSection, optimisticData) => {
            return optimisticData.map(x => (x.id === inclusionSection.id ? inclusionSection : x));
        },
    }
);

export const useInclusionSectionDelete = factory.mutate(
    (entity: { id: string; studyId: string }): Promise<RevisionedCommandResponse<CommandResponse>> => {
        return axios
            .delete(`/api/study-designer/studies/${entity.studyId}/inclusion-sections/${entity.id}`)
            .then(response => response.data);
    }
);

export const useInclusionSectionMove = factory.mutate(
    (entity: {
        id: string;
        studyId: string;
        placement: PlacementPayload;
    }): Promise<RevisionedCommandResponse<CommandResponse>> => {
        const { id, studyId, placement } = entity;
        return axios
            .put(`/api/study-designer/studies/${studyId}/inclusion-sections/${id}/actions/move`, placement)
            .then(response => response.data);
    }
);

export const useInclusionSectionLink = factory.mutate(
    (entity: {
        id: string;
        studyId: string;
        linkedSection: Partial<StudySection> & Pick<StudySection, 'id' | 'studyId' | 'entityType'>;
    }): Promise<RevisionedCommandResponse<CommandResponse>> => {
        const { id, studyId, linkedSection } = entity;
        return axios
            .put(`/api/study-designer/studies/${studyId}/inclusion-sections/${id}/actions/link`, linkedSection)
            .then(response => response.data);
    }
);

export const useInclusionSectionRename = factory.mutate(
    (entity: {
        id: string;
        studyId: string;
        renamePayload: RenamePayload;
    }): Promise<RevisionedCommandResponse<CommandResponse>> => {
        const { id, studyId, renamePayload } = entity;
        return axios
            .put(`/api/study-designer/studies/${studyId}/inclusion-sections/${id}/actions/rename`, renamePayload)
            .then(response => response.data);
    }
);
